import { useEffect, useMemo, useRef, useState } from 'react';
import { FiGlobe, FiSearch, FiCheck } from 'react-icons/fi';
import { getLanguageOptions, getLanguageLabel } from '../utils/translate';

export default function LanguageSelector({ value, onChange, title = 'Translate chat' }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const options = useMemo(() => getLanguageOptions(), []);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((l) => l.label.toLowerCase().includes(q));
  }, [options, query]);

  useEffect(() => {
    function onClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const handleSelect = (code) => {
    onChange(code);
    setOpen(false);
    setQuery('');
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white border border-neutral-200 text-neutral-700 text-xs font-semibold hover:bg-neutral-50 transition-colors"
        title={title}
      >
        <FiGlobe className="w-3.5 h-3.5" />
        <span className="max-w-[80px] truncate">{getLanguageLabel(value)}</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-lg shadow-lg border border-neutral-200 z-50 overflow-hidden">
          <div className="p-2 border-b border-neutral-100">
            <div className="flex items-center gap-2 px-2 py-1.5 bg-neutral-100 rounded-md">
              <FiSearch className="w-3.5 h-3.5 text-neutral-400" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search language…"
                className="flex-1 bg-transparent text-xs text-neutral-800 placeholder-neutral-400 outline-none"
              />
            </div>
          </div>
          <div className="max-h-60 overflow-y-auto">
            {filtered.length === 0 && (
              <p className="px-3 py-2 text-xs text-neutral-500">No language found</p>
            )}
            {filtered.map((l) => (
              <button
                key={l.code}
                type="button"
                onClick={() => handleSelect(l.code)}
                className="w-full text-left px-3 py-2 text-xs text-neutral-700 hover:bg-emerald-50 flex items-center justify-between"
              >
                <span>{l.label}</span>
                {l.code === value && <FiCheck className="w-3.5 h-3.5 text-emerald-600" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
